import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, RotateCcw, ChevronDown, ChevronRight, Download } from "lucide-react";
import { api, downloadFile } from "@/lib/api";
import { FilterSelect, DateInput, Pager, DARK_INPUT, fmtDate, errMsg, useDebounced, useLatest } from "./shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { q: "", actor_id: "", action: "", role: "", date_from: "", date_to: "" };
const SENSITIVE = /delete|refund|pricing|payment_gateway|mfa|password|dsc|agreement|admin_user|integration|disabled/;

export default function AuditLogs() {
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const q = useDebounced(f.q);
  const begin = useLatest();
  const load = useCallback(async () => {
    const isLatest = begin();
    try {
      const params = Object.fromEntries(Object.entries({ ...f, q, page, page_size: 50 }).filter(([, v]) => v !== ""));
      const { data } = await api.get("/admin/audit-logs", { params });
      if (isLatest()) setData(data);
    } catch (e) { if (isLatest()) { toast.error(errMsg(e)); setData({ items: [], total: 0, facets: {} }); } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.actor_id, f.action, f.role, f.date_from, f.date_to, q, page]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };
  const fc = data?.facets || {};

  return (
    <div data-testid="audit-logs-page">
      <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Super Admin · Compliance</div>
      <h1 className="font-display text-3xl font-bold mt-1">Audit Log</h1>
      <p className="text-sm text-slate-400 mt-1 mb-6">Every sensitive action — who did it, when and what changed. Records cannot be edited or deleted.</p>
      <div className="admin-card p-3 mb-4 flex flex-col md:flex-row md:flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
          <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Action, booking code, email…" className={`${DARK_INPUT} pl-9 h-9`} data-testid="audit-search-input"/>
        </div>
        <Select value={f.actor_id || "all"} onValueChange={(v) => set("actor_id")(v === "all" ? "" : v)}>
          <SelectTrigger className={`${DARK_INPUT} h-9 w-full md:w-52`} data-testid="audit-filter-actor"><SelectValue placeholder="All people"/></SelectTrigger>
          <SelectContent><SelectItem value="all">All people</SelectItem>{(fc.actors || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}</SelectContent>
        </Select>
        <FilterSelect value={f.action} onChange={set("action")} options={fc.action_types || []} placeholder="All types" testid="audit-filter-type"/>
        <FilterSelect value={f.role} onChange={set("role")} options={fc.roles || []} placeholder="All roles" testid="audit-filter-role"/>
        <DateInput value={f.date_from} onChange={set("date_from")} testid="audit-filter-from"/>
        <DateInput value={f.date_to} onChange={set("date_to")} testid="audit-filter-to"/>
        <Button variant="ghost" size="sm" className="text-slate-400 h-9" onClick={() => { setF(EMPTY); setPage(1); }} data-testid="audit-filter-reset"><RotateCcw size={14} className="mr-1"/>Reset</Button>
        <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-9" data-testid="audit-export-csv" onClick={() => {
          const qs = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""))).toString();
          downloadFile(`/admin/audit-logs/export.csv${qs ? `?${qs}` : ""}`, `karmivo_audit_log_${new Date().toISOString().slice(0, 10)}.csv`)
            .then(() => toast.success(`Exported ${data?.total ?? ""} entries`)).catch(() => toast.error("Export failed"));
        }}><Download size={14} className="mr-1"/>Export CSV</Button>
      </div>
      {!data ? <div className="text-slate-500 text-sm">Loading…</div> : !data.items.length ? <div className="admin-card p-10 text-center text-slate-500" data-testid="audit-empty">No audit entries match.</div> : (
        <div className="admin-card overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-800/70 text-slate-400 text-[10px] uppercase tracking-widest"><tr><th className="p-3 w-8"></th><th className="text-left">Time</th><th className="text-left">Person</th><th className="text-left">Role</th><th className="text-left">Action</th></tr></thead>
            <tbody>
              {data.items.map((a) => (
                <FragmentRow key={a.id} a={a} open={open === a.id} onToggle={() => setOpen(open === a.id ? null : a.id)}/>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pager page={page} pageSize={50} total={data.total} onPage={setPage} testid="audit-pager"/>}
    </div>
  );
}

function FragmentRow({ a, open, onToggle }) {
  return (
    <>
      <tr className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer" onClick={onToggle} data-testid={`audit-row-${a.id}`}>
        <td className="p-3 text-slate-500">{open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</td>
        <td className="font-mono text-xs text-slate-400">{fmtDate(a.ts)}</td>
        <td><div className="text-slate-200 text-xs">{a.actor_name}</div><div className="text-[11px] text-slate-500">{a.actor_email}</div></td>
        <td className="text-xs text-slate-400">{a.role}</td>
        <td className={`font-mono text-xs font-bold ${SENSITIVE.test(a.action) ? "text-orange-300" : "text-slate-300"}`}>{a.action}</td>
      </tr>
      {open && <tr className="bg-slate-950/60"><td colSpan={5} className="p-3"><pre className="text-[11px] text-slate-400 whitespace-pre-wrap break-all" data-testid="audit-details">{JSON.stringify(a.details, null, 2)}</pre></td></tr>}
    </>
  );
}
